import type { Room } from "building-types/rooms/roomTypes";
import { WallLayerType, WallType } from "building-types/walls/wallEnums";
import type { Wall } from "building-types/walls/wallTypes";
import { getWallPath } from "building-types/walls/wallUtils";
import { DIST_EPSILON, Line2Side } from "geom/core";
import { line2Side } from "geom/line2";
import { offsetPolyLine2Variable } from "geom/offset";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist, vec2Result } from "geom/vec2";
import { solidWallLayerWithFallback } from "./wallLayers";

function wallCenterLineStart(hostRoom: Room, wall: Wall) {
  const path = getWallPath(wall);

  if (path.leftRoom === hostRoom) {
    vec2Result[0] = path.startX;
    vec2Result[1] = path.startY;
  } else {
    vec2Result[0] = path.endX;
    vec2Result[1] = path.endY;
  }

  return vec2Result;
}

function wallCenterLineEnd(hostRoom: Room, wall: Wall) {
  const path = getWallPath(wall);

  if (path.leftRoom === hostRoom) {
    vec2Result[0] = path.endX;
    vec2Result[1] = path.endY;
  } else {
    vec2Result[0] = path.startX;
    vec2Result[1] = path.startY;
  }

  return vec2Result;
}

function wallNext(walls: Wall[], index: number) {
  return walls[(index + 1 + walls.length) % walls.length];
}

function wallPrev(walls: Wall[], index: number) {
  return walls[(index - 1 + walls.length) % walls.length];
}

/**
 * wallBaselineOffset is an approximation of the perimeter offset of a wall at a room floor edge.
 * Void walls are explicitly handled as having zero offsets for this calculation in order to align with adjacent room edges
 * between open/connected rooms
 */
export function wallBaselineOffset(wall: Wall, room: Room) {
  if (wall.wallType === WallType.VOID) {
    return 0;
  }

  const { leftOffset, rightOffset } = solidWallLayerWithFallback(wall, WallLayerType.MASS);
  const path = getWallPath(wall);

  if (!path.leftRoom) {
    return path.rightRoom?.id === room.id ? rightOffset : leftOffset;
  }

  return path.leftRoom.id === room.id ? leftOffset : rightOffset;
}

/**
 * wallBaselineOffset is an approximation of the perimeter polyline of a wall at a set of room floor edges.
 * Void walls are explicitly handled as having zero offsets for this calculation in order to align with adjacent room edges
 * between open/connected rooms
 */
export function wallBaseLine(
  room: Room,
  roomWalls: Wall[],
  index: number,
  removeShortSegmentsAtEnds: boolean = false,
): PolyLine2 | null {
  // get neighbor walls
  const wall = roomWalls[index];
  const pwall = wallPrev(roomWalls, index);
  const nwall = wallNext(roomWalls, index);

  // get the wall ends in order and form a polyline
  const [ax, ay] = wallCenterLineStart(room, pwall);
  const [bx, by] = wallCenterLineStart(room, wall);
  const [cx, cy] = wallCenterLineStart(room, nwall);
  const [dx, dy] = wallCenterLineEnd(room, nwall);

  const pl = polyLine2New([ax, ay, bx, by, cx, cy, dx, dy]);

  const offset = -wallBaselineOffset(wall, room);
  const prevCollinear = line2Side(ax, ay, cx, cy, bx, by) === Line2Side.COLLINEAR;
  const nextCollinear = line2Side(bx, by, dx, dy, cx, cy) === Line2Side.COLLINEAR;

  const poffset = prevCollinear ? offset : -wallBaselineOffset(pwall, room);
  const noffset = nextCollinear ? offset : -wallBaselineOffset(nwall, room);

  if (removeShortSegmentsAtEnds) {
    let wallMaxOffset = 0;
    if (!prevCollinear && !nextCollinear) {
      wallMaxOffset = -Math.min(poffset, noffset);
    } else if (!prevCollinear) {
      wallMaxOffset = -poffset;
    } else if (!nextCollinear) {
      wallMaxOffset = -noffset;
    }

    if (wallMaxOffset > DIST_EPSILON && vec2Dist(bx, by, cx, cy) < wallMaxOffset + DIST_EPSILON) {
      return null;
    }
  }

  // offset the polyline according to the wall offsets
  const dists = [poffset, offset, noffset];
  const baselines = offsetPolyLine2Variable(pl, dists);

  // extract the inner edge
  return baselines.slice(2, 6);
}
